import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, RotateCcw, Save, Wand2, X } from "lucide-react";
import { toast } from "sonner";

const PURPLE = "#5B2383";

/* ---------- ficha de jugador ---------- */
const Chip = ({ p, size = "md", color = "purple", selected, onClick, draggable, onDragStart, slotLabel, testid }) => {
  const dim = size === "sm" ? "w-9 h-9 text-[11px]" : "w-11 h-11 text-sm sm:w-12 sm:h-12";
  const bg = color === "orange" ? "bg-amber-600" : "bg-purple-700";
  return (
    <button
      type="button"
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
      className={`flex flex-col items-center gap-0.5 focus:outline-none ${onClick ? "cursor-pointer" : "cursor-default"}`}
      data-testid={testid}
    >
      <span className={`${dim} ${bg} rounded-full border-2 flex items-center justify-center font-black text-white shadow-md transition-transform ${selected ? "border-yellow-300 scale-110 ring-2 ring-yellow-300/60" : "border-white"}`}>
        {p.dorsal ?? "-"}
      </span>
      <span className="text-[9px] sm:text-[10px] font-bold text-white leading-none drop-shadow max-w-[64px] truncate">
        {(p.name || "").split(" ")[0]}
      </span>
      {slotLabel && <span className="text-[8px] font-bold text-purple-200 leading-none">{slotLabel}</span>}
    </button>
  );
};

/* ---------- líneas del campo ---------- */
const PitchLines = () => (
  <>
    <div className="absolute inset-[3%] border-2 border-white/70 rounded-sm pointer-events-none" />
    <div className="absolute left-[3%] right-[3%] top-1/2 border-t-2 border-white/70 pointer-events-none" />
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[26%] aspect-square rounded-full border-2 border-white/70 pointer-events-none" />
    <div className="absolute left-1/2 -translate-x-1/2 top-[3%] w-[44%] h-[13%] border-2 border-t-0 border-white/70 pointer-events-none" />
    <div className="absolute left-1/2 -translate-x-1/2 bottom-[3%] w-[44%] h-[13%] border-2 border-b-0 border-white/70 pointer-events-none" />
  </>
);

export default function LineupEditor({ matchId, canEdit }) {
  const [templates, setTemplates] = useState(null);
  const [byCategory, setByCategory] = useState({});
  const [teamCategory, setTeamCategory] = useState("");
  const [formationIds, setFormationIds] = useState([]);
  const [data, setData] = useState(null); // respuesta del GET /lineup
  const [assign, setAssign] = useState({}); // slot -> player obj
  const [formationId, setFormationId] = useState("");
  const [sel, setSel] = useState(null); // {type:"player"|"slot", id}
  const [slotPicker, setSlotPicker] = useState(null); // slot code para diálogo
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const tpl = templates?.[formationId];
  const titulares = useMemo(() => {
    if (!data) return [];
    const placed = data.starters || [];
    return [...placed, ...(data.unassigned || [])].map((p) => ({
      player_id: p.player_id, name: p.name, dorsal: p.dorsal, position: p.position,
    }));
  }, [data]);

  const applyResponse = useCallback((d) => {
    setData(d);
    setFormationId(d.formation_id);
    const map = {};
    (d.starters || []).forEach((st) => { map[st.slot] = st; });
    setAssign(map);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [metaR, luR] = await Promise.all([
        api.get("/meta/formations"),
        api.get(`/matches/${matchId}/lineup`),
      ]);
      setTemplates(metaR.data.templates);
      setByCategory(metaR.data.by_category || {});
      applyResponse(luR.data);
      setDirty(false);
      setTeamCategory(luR.data.team_category || "");
      setFormationIds(luR.data.formation_ids || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [matchId, applyResponse]);

  useEffect(() => { load(); }, [load]);

  const availableFormations = useMemo(() => {
    if (!templates) return [];
    if (formationIds.length) return formationIds;
    const ids = byCategory[teamCategory];
    return ids && ids.length ? ids : Object.keys(templates);
  }, [templates, byCategory, teamCategory, formationIds]);

  const unplaced = useMemo(() => {
    const placedIds = new Set(Object.values(assign).map((p) => p.player_id));
    return titulares.filter((t) => !placedIds.has(t.player_id));
  }, [assign, titulares]);

  const playerById = (id) => titulares.find((t) => t.player_id === id);

  /* ---------- acciones ---------- */
  const placePlayer = (playerId, slotCode) => {
    if (!canEdit) return;
    const p = playerById(playerId);
    if (!p) return;
    setAssign((a) => {
      const next = { ...a };
      // si ya estaba en otro puesto, lo quitamos de allí
      for (const [code, pl] of Object.entries(next)) {
        if (pl.player_id === playerId) delete next[code];
      }
      next[slotCode] = { ...p, slot: slotCode };
      return next;
    });
    setDirty(true);
    setSel(null);
    setSlotPicker(null);
  };

  const swapSlots = (a, b) => {
    setAssign((m) => {
      const next = { ...m };
      const pa = next[a]; const pb = next[b];
      if (pa) next[b] = { ...pa, slot: b }; else delete next[b];
      if (pb) next[a] = { ...pb, slot: a }; else delete next[a];
      return next;
    });
    setDirty(true);
    setSel(null);
  };

  const removeFromSlot = (code) => {
    setAssign((m) => { const n = { ...m }; delete n[code]; return n; });
    setDirty(true);
    setSel(null);
  };

  const onSlotTap = (code) => {
    if (!canEdit) return;
    const occupied = assign[code];
    if (sel?.type === "player") { placePlayer(sel.id, code); return; }
    if (sel?.type === "slot") {
      if (sel.id !== code) swapSlots(sel.id, code); else setSel(null);
      return;
    }
    if (occupied) setSel({ type: "slot", id: code });
    else setSlotPicker(code);
  };

  const autoComplete = async () => {
    try {
      const { data: d } = await api.post(`/matches/${matchId}/lineup/proposal`, { formation_id: formationId });
      applyResponse(d);
      setDirty(true);
      toast.info("Propuesta automática aplicada — revisa y guarda");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeFormation = async (fid) => {
    if (fid === formationId) return;
    try {
      const currentIds = Object.values(assign).map((p) => p.player_id);
      const { data: d } = await api.post(`/matches/${matchId}/lineup/proposal`, {
        formation_id: fid, player_ids: currentIds.length ? currentIds : null,
      });
      applyResponse(d);
      setDirty(true);
      toast.warning("Sistema cambiado: distribución recolocada como propuesta. Revisa antes de guardar.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetAll = () => { setAssign({}); setDirty(true); setSel(null); };

  const save = async () => {
    setSaving(true);
    try {
      const starters = Object.values(assign).map((p) => ({ player_id: p.player_id, slot: p.slot }));
      const gks = starters.filter((s) => (playerById(s.player_id)?.position || "").toLowerCase().startsWith("portero"));
      let confirmMulti = false;
      if (gks.length > 1) {
        confirmMulti = window.confirm("Hay más de un portero titular en el campo. ¿Confirmas la alineación?");
        if (!confirmMulti) { setSaving(false); return; }
      }
      const { data: r } = await api.put(`/matches/${matchId}/lineup`, {
        formation_id: formationId, starters, confirm_multi_gk: confirmMulti,
      });
      setDirty(false);
      setData((d) => ({ ...d, status: "confirmada", saved: true }));
      toast.success(r.vacantes > 0
        ? `Alineación guardada (${r.vacantes} posición(es) sin cubrir)`
        : "Alineación confirmada y guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  /* ---------- drag & drop (escritorio) ---------- */
  const onDropSlot = (e, code) => {
    e.preventDefault();
    const pid = e.dataTransfer.getData("text/player");
    if (pid) placePlayer(pid, code);
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>;
  if (!data || !tpl) return <div className="p-6 text-sm text-slate-500">No se pudo cargar la alineación.</div>;

  const pending = dirty || data.status === "pendiente_revision";
  const vacantes = tpl.slots.filter((s) => !assign[s.code]).length;

  return (
    <div className="space-y-4" data-testid="lineup-editor">
      {/* Controles */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-40">
          <Select value={formationId} onValueChange={changeFormation} disabled={!canEdit}>
            <SelectTrigger data-testid="lineup-formation-select"><SelectValue placeholder="Sistema" /></SelectTrigger>
            <SelectContent>
              {availableFormations.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <span
          className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${pending ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}
          data-testid="lineup-status-badge"
        >
          {pending ? "Pendiente de revisar" : "Alineación confirmada"}
        </span>
        {vacantes > 0 && (
          <span className="text-[10px] font-bold text-slate-500" data-testid="lineup-vacantes">
            {vacantes} posición(es) vacía(s)
          </span>
        )}
      </div>

      {titulares.length === 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          No hay titulares en la convocatoria. Marca titulares en la pestaña "Partido" para poder colocarlos.
        </div>
      )}

      {/* Campo */}
      <div className="lineup-split">
      <div className="mx-auto w-full max-w-md lineup-pitch-col">
        <div
          className="relative w-full rounded-lg overflow-hidden shadow-lg"
          style={{ aspectRatio: "2 / 2.9", background: "linear-gradient(180deg, #15803D 0%, #166534 100%)" }}
          data-testid="lineup-pitch"
        >
          <PitchLines />
          {tpl.slots.map((s) => {
            const p = assign[s.code];
            const isSel = sel?.type === "slot" && sel.id === s.code;
            return (
              <div
                key={s.code}
                className="absolute -translate-x-1/2 -translate-y-1/2 z-10"
                style={{ left: `${s.x}%`, top: `${s.y}%` }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDropSlot(e, s.code)}
              >
                {p ? (
                  <Chip
                    p={p}
                    selected={isSel}
                    onClick={() => onSlotTap(s.code)}
                    draggable={canEdit}
                    onDragStart={(e) => e.dataTransfer.setData("text/player", p.player_id)}
                    slotLabel={s.code}
                    testid={`slot-${s.code}`}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => onSlotTap(s.code)}
                    className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full border-2 border-dashed flex items-center justify-center text-[9px] font-black transition ${sel ? "border-yellow-300 text-yellow-200 bg-yellow-300/10 animate-pulse" : "border-white/60 text-white/80 bg-white/5 hover:bg-white/15"}`}
                    data-testid={`slot-${s.code}`}
                    title={s.label}
                  >
                    {s.code}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Banquillo (suplentes, no colocables) */}
        {(data.substitutes || []).length > 0 && (
          <div className="mt-1 rounded-lg p-2.5" style={{ backgroundColor: "#0B3F22" }} data-testid="lineup-bench">
            <div className="text-[9px] font-black uppercase tracking-widest text-yellow-300 mb-1.5">Banquillo</div>
            <div className="flex flex-wrap gap-2.5">
              {data.substitutes.map((p) => (
                <Chip key={p.player_id} p={p} size="sm" color="orange" testid={`bench-${p.player_id}`} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="lineup-side-col mt-4 space-y-4">
      {/* Titulares sin colocar */}
      {unplaced.length > 0 && (
        <Card className="border-purple-200">
          <CardContent className="p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-purple-700 mb-2">
              Titulares sin posición ({unplaced.length}) — toca un jugador y luego un puesto
            </div>
            <div className="flex flex-wrap gap-2">
              {unplaced.map((p) => (
                <button
                  key={p.player_id}
                  type="button"
                  onClick={() => canEdit && setSel(sel?.type === "player" && sel.id === p.player_id ? null : { type: "player", id: p.player_id })}
                  draggable={canEdit}
                  onDragStart={(e) => e.dataTransfer.setData("text/player", p.player_id)}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-full border text-xs font-bold transition ${sel?.type === "player" && sel.id === p.player_id ? "border-yellow-400 bg-yellow-50 text-yellow-800 ring-2 ring-yellow-300/60" : "border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100"}`}
                  data-testid={`unplaced-${p.player_id}`}
                >
                  <span className="w-6 h-6 rounded-full bg-purple-700 text-white flex items-center justify-center text-[10px] font-black">{p.dorsal ?? "-"}</span>
                  {(p.name || "").split(" ")[0]}
                  <span className="text-[9px] text-slate-400 font-semibold">{p.position || ""}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Barra de acciones al seleccionar un puesto ocupado */}
      {sel?.type === "slot" && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-2.5 text-xs font-bold text-yellow-900" data-testid="lineup-slot-actions">
          {assign[sel.id]?.name?.split(" ")[0]} en {sel.id}: toca otro puesto para intercambiar, o
          <Button size="sm" variant="outline" onClick={() => removeFromSlot(sel.id)} className="h-7 text-xs" data-testid="lineup-remove-btn">
            <X className="h-3.5 w-3.5 mr-1" /> Quitar de la posición
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSel(null)} className="h-7 text-xs">Cancelar</Button>
        </div>
      )}

      {/* Acciones */}
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={autoComplete} className="border-purple-300 text-purple-700" data-testid="lineup-autocomplete-btn">
            <Wand2 className="h-4 w-4 mr-1.5" /> Completar automáticamente
          </Button>
          <Button variant="outline" onClick={resetAll} data-testid="lineup-reset-btn">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Restablecer
          </Button>
          <Button onClick={save} disabled={saving} className="text-white font-bold" style={{ backgroundColor: PURPLE }} data-testid="lineup-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Guardar alineación
          </Button>
        </div>
      )}
      </div>
      </div>

      {/* Selector de jugador al tocar un puesto vacío */}
      <Dialog open={Boolean(slotPicker)} onOpenChange={(o) => !o && setSlotPicker(null)}>
        <DialogContent className="max-w-sm" data-testid="lineup-slot-picker">
          <DialogHeader>
            <DialogTitle>Asignar jugador a {slotPicker}</DialogTitle>
            <DialogDescription className="text-xs">
              {tpl.slots.find((s) => s.code === slotPicker)?.label || ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {unplaced.length === 0 && <div className="text-sm text-slate-400 italic">No quedan titulares sin colocar.</div>}
            {unplaced.map((p) => {
              const compat = (tpl.slots.find((s) => s.code === slotPicker)?.compat || [])
                .some((c) => (p.position || "").toLowerCase().includes(c.toLowerCase()) || c.toLowerCase().includes((p.position || "").toLowerCase()) && p.position);
              return (
                <button
                  key={p.player_id}
                  type="button"
                  onClick={() => placePlayer(p.player_id, slotPicker)}
                  className="w-full flex items-center gap-3 p-2 rounded-md border border-slate-200 hover:bg-purple-50 text-left"
                  data-testid={`picker-${p.player_id}`}
                >
                  <span className="w-8 h-8 rounded-full bg-purple-700 text-white flex items-center justify-center text-xs font-black shrink-0">{p.dorsal ?? "-"}</span>
                  <span className="flex-1 text-sm font-bold text-slate-800 truncate">{p.name}</span>
                  <span className={`text-[10px] font-bold ${compat ? "text-emerald-600" : "text-slate-400"}`}>{p.position || "—"}</span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
